import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import FirstLoginSetupModal from "./FirstLoginSetupModal";
import useEscapeKey from "../hooks/useEscapeKey";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const location = useLocation();

  useEffect(() => {
    let isDisposed = false;
    let objectUrl = null;

    const setFavicon = (href) => {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "icon");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    };

    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const loadCompanyLogoAsFavicon = async () => {
      try {
        const response = await fetch("/api/company-settings/logo-image", {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });

        if (!response.ok) return;
        const blob = await response.blob();
        if (!blob || blob.size === 0 || isDisposed) return;
        objectUrl = URL.createObjectURL(blob);
        setFavicon(objectUrl);
      } catch {
        // Keep current favicon if logo endpoint is unavailable.
      }
    };

    loadCompanyLogoAsFavicon();

    return () => {
      isDisposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (event) => setIsMobile(event.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEscapeKey(() => setMobileSidebarOpen(false), mobileSidebarOpen);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen((prev) => !prev);
    } else {
      setSidebarOpen((prev) => !prev);
    }
  };

  return (
    <div className={`min-h-screen bg-adminlte-light ${sidebarOpen ? "" : "sidebar-collapsed"}`}>
      <Navbar onToggleSidebar={toggleSidebar} />
      <Sidebar
        open={sidebarOpen}
        mobileOpen={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        isMobile={isMobile}
      />
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={`transition-all duration-300 pt-14 ${
          sidebarOpen ? "md:ml-[250px]" : "md:ml-[64px]"
        }`}
      >
        <main className="p-5">
          <Outlet />
        </main>
        <footer className="border-t border-gray-200 bg-white px-4 py-3 text-xs text-gray-500 flex justify-between">
          <span>&copy; {new Date().getFullYear()} <strong>ALG Intelligence</strong></span>
          <span>v1.0.0</span>
        </footer>
      </div>
      <FirstLoginSetupModal />
    </div>
  );
}
